import React, { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import { uploadAPI, productoMediaAPI, getImageUrl } from '../services/api';
import ImageLightbox from './ImageLightbox';

const ProductMediaManager = ({ productoId, onMediaChange }) => {
  const [imagenes, setImagenes] = useState([]);
  const [videos, setVideos] = useState([]);
  const [stats, setStats] = useState({ imagenes_count: 0, imagenes_max: 20, videos_count: 0, videos_max: 6 });
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null); // { current, total, type }
  const [lightboxData, setLightboxData] = useState(null); // { items, initialIndex }
  const [draggedItem, setDraggedItem] = useState(null);

  const imageInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  // Cargar media del producto
  const loadMedia = useCallback(async () => {
    if (!productoId) return;
    try {
      setLoading(true);
      const res = await productoMediaAPI.getMedia(productoId);
      if (res.data.success) {
        setImagenes(res.data.data.imagenes || []);
        setVideos(res.data.data.videos || []);
        setStats(res.data.data.stats || {});
      }
    } catch (err) {
      console.error('Error cargando media:', err);
    } finally {
      setLoading(false);
    }
  }, [productoId]);

  useEffect(() => {
    loadMedia();
  }, [loadMedia]);

  // Upload de imagenes (puede ser multiple)
  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    const available = stats.imagenes_max - stats.imagenes_count;
    if (files.length > available) {
      toast.warning(`Solo puedes agregar ${available} imagen(es) mas (maximo ${stats.imagenes_max})`);
      return;
    }

    setUploading(true);
    let uploaded = 0;
    setUploadProgress({ current: 0, total: files.length, type: 'imagen' });

    for (const file of files) {
      try {
        // 1. Subir imagen (se convierte a PNG automaticamente)
        const uploadRes = await uploadAPI.upload(file);
        if (uploadRes.data.success) {
          const data = uploadRes.data.data;
          // 2. Registrar en producto_media
          await productoMediaAPI.addMedia(productoId, {
            tipo: 'imagen',
            url: data.url,
            filename: data.filename,
            original_filename: data.original_filename,
            mimetype: data.mimetype || 'image/png',
            size_bytes: data.size_bytes,
            width: data.width,
            height: data.height
          });
          uploaded++;
          setUploadProgress({ current: uploaded, total: files.length, type: 'imagen' });
        }
      } catch (err) {
        console.error('Error subiendo imagen:', err);
        toast.error(`Error al subir: ${file.name}`);
      }
    }

    if (uploaded > 0) {
      toast.success(`${uploaded} imagen(es) subida(s) como PNG`);
      await loadMedia();
      if (onMediaChange) onMediaChange();
    }

    setUploading(false);
    setUploadProgress(null);
    if (imageInputRef.current) imageInputRef.current.value = '';
    if (cameraInputRef.current) cameraInputRef.current.value = '';
  };

  // Upload de video
  const handleVideoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (stats.videos_count >= stats.videos_max) {
      toast.warning(`Maximo ${stats.videos_max} videos por producto`);
      return;
    }

    setUploading(true);
    setUploadProgress({ current: 0, total: 1, type: 'video' });

    try {
      const uploadRes = await uploadAPI.uploadVideo(file);
      if (uploadRes.data.success) {
        const data = uploadRes.data.data;
        await productoMediaAPI.addMedia(productoId, {
          tipo: 'video',
          url: data.url,
          filename: data.filename,
          original_filename: data.original_filename,
          mimetype: data.mimetype,
          size_bytes: data.size_bytes,
          duration_seconds: data.duration_seconds
        });
        toast.success('Video subido correctamente');
        setUploadProgress({ current: 1, total: 1, type: 'video' });
        await loadMedia();
        if (onMediaChange) onMediaChange();
      }
    } catch (err) {
      console.error('Error subiendo video:', err);
      const msg = err.response?.data?.error || 'Error al subir video';
      toast.error(msg);
    }

    setUploading(false);
    setUploadProgress(null);
    if (videoInputRef.current) videoInputRef.current.value = '';
  };

  // Eliminar media
  const handleDelete = async (media, tipo) => {
    if (!window.confirm(`Eliminar ${tipo === 'imagen' ? 'imagen' : 'video'}?`)) return;
    try {
      await productoMediaAPI.deleteMedia(media.id);
      toast.success(`${tipo === 'imagen' ? 'Imagen' : 'Video'} eliminado`);
      await loadMedia();
      if (onMediaChange) onMediaChange();
    } catch (err) {
      toast.error('Error al eliminar');
    }
  };

  // Marcar como principal
  const handleSetPrincipal = async (media) => {
    try {
      await productoMediaAPI.setPrincipal(media.id);
      toast.success('Imagen principal actualizada');
      await loadMedia();
      if (onMediaChange) onMediaChange();
    } catch (err) {
      toast.error('Error al cambiar imagen principal');
    }
  };

  // Drag & Drop para reordenar imagenes
  const handleDragStart = (e, item) => {
    setDraggedItem(item);
    e.dataTransfer.effectAllowed = 'move';
    e.target.style.opacity = '0.5';
  };

  const handleDragEnd = (e) => {
    e.target.style.opacity = '1';
    setDraggedItem(null);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, targetItem) => {
    e.preventDefault();
    if (!draggedItem || draggedItem.id === targetItem.id) return;

    // Reordenar localmente
    const newImagenes = [...imagenes];
    const dragIndex = newImagenes.findIndex(i => i.id === draggedItem.id);
    const targetIndex = newImagenes.findIndex(i => i.id === targetItem.id);

    newImagenes.splice(dragIndex, 1);
    newImagenes.splice(targetIndex, 0, draggedItem);

    // Actualizar orden
    const items = newImagenes.map((img, idx) => ({ id: img.id, orden: idx }));
    setImagenes(newImagenes.map((img, idx) => ({ ...img, orden: idx })));

    try {
      await productoMediaAPI.reorder(productoId, items);
    } catch (err) {
      console.error('Error reordenando:', err);
      await loadMedia(); // Revertir si falla
    }
  };

  // Abrir lightbox
  const openLightbox = (index) => {
    const items = imagenes.map(img => ({
      type: 'image',
      url: getImageUrl(img.url),
      es_principal: img.es_principal
    }));
    setLightboxData({ items, initialIndex: index });
  };

  const openVideoLightbox = (index) => {
    const items = videos.map(v => ({
      type: 'video',
      url: getImageUrl(v.url),
      duration: v.duration_seconds
    }));
    setLightboxData({ items, initialIndex: index });
  };

  // Formatear duracion
  const formatDuration = (seconds) => {
    if (!seconds) return '';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Formatear bytes
  const formatBytes = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / 1024 / 1024).toFixed(1) + ' MB';
  };

  if (!productoId) {
    return (
      <div className="text-center py-4 text-muted">
        <i className="bi bi-info-circle fs-3 d-block mb-2"></i>
        <p>Guarda el producto primero para agregar fotos y videos</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="text-center py-4">
        <div className="spinner-border spinner-border-sm text-primary me-2" role="status"></div>
        Cargando media...
      </div>
    );
  }

  return (
    <div className="product-media-manager">
      {/* ═══ SECCIÓN FOTOS ═══ */}
      <div className="mb-4">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h6 className="mb-0">
              <i className="bi bi-images me-2"></i>
              Fotos
              <span className="badge bg-primary ms-2">{stats.imagenes_count} / {stats.imagenes_max}</span>
            </h6>
            <small className="text-muted">Todas las imagenes se convierten a PNG automaticamente</small>
          </div>
          <div className="d-flex gap-2">
            <button
              type="button"
              className="btn btn-outline-primary btn-sm"
              onClick={() => cameraInputRef.current?.click()}
              disabled={uploading || stats.imagenes_count >= stats.imagenes_max}
              title="Tomar foto con camara"
            >
              <i className="bi bi-camera me-1"></i>Camara
            </button>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={() => imageInputRef.current?.click()}
              disabled={uploading || stats.imagenes_count >= stats.imagenes_max}
            >
              <i className="bi bi-plus-lg me-1"></i>Agregar Fotos
            </button>
          </div>
          <input
            type="file"
            ref={imageInputRef}
            accept="image/jpeg,image/png,image/gif,image/webp"
            multiple
            onChange={handleImageUpload}
            className="d-none"
          />
          <input
            type="file"
            ref={cameraInputRef}
            accept="image/*"
            capture="environment"
            onChange={handleImageUpload}
            className="d-none"
          />
        </div>

        {/* Progress bar */}
        {uploadProgress && uploadProgress.type === 'imagen' && (
          <div className="progress mb-3" style={{ height: '20px' }}>
            <div
              className="progress-bar progress-bar-striped progress-bar-animated bg-primary"
              style={{ width: `${(uploadProgress.current / uploadProgress.total) * 100}%` }}
            >
              {uploadProgress.current} / {uploadProgress.total}
            </div>
          </div>
        )}

        {/* Grid de imagenes */}
        {imagenes.length > 0 ? (
          <div className="row g-2">
            {imagenes.map((img, idx) => (
              <div key={img.id} className="col-6 col-md-3">
                <div
                  className={`media-card position-relative rounded border ${img.es_principal ? 'border-warning border-2' : ''}`}
                  draggable
                  onDragStart={(e) => handleDragStart(e, img)}
                  onDragEnd={handleDragEnd}
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, img)}
                  style={{ cursor: 'grab', overflow: 'hidden' }}
                >
                  <img
                    src={getImageUrl(img.url)}
                    alt={img.original_filename || `Foto ${idx + 1}`}
                    className="w-100"
                    style={{ height: '120px', objectFit: 'cover', cursor: 'pointer' }}
                    onClick={() => openLightbox(idx)}
                  />

                  {/* Badge principal */}
                  {img.es_principal && (
                    <span className="position-absolute top-0 start-0 badge bg-warning text-dark m-1" style={{ fontSize: '0.65rem' }}>
                      <i className="bi bi-star-fill me-1"></i>Principal
                    </span>
                  )}

                  {/* Overlay de acciones */}
                  <div className="media-overlay position-absolute bottom-0 start-0 end-0 d-flex justify-content-between p-1"
                       style={{ background: 'linear-gradient(transparent, rgba(0,0,0,0.7))' }}>
                    {!img.es_principal && (
                      <button
                        type="button"
                        className="btn btn-sm text-white p-0 px-1"
                        onClick={() => handleSetPrincipal(img)}
                        title="Marcar como principal"
                      >
                        <i className="bi bi-star"></i>
                      </button>
                    )}
                    <span></span>
                    <button
                      type="button"
                      className="btn btn-sm text-danger p-0 px-1"
                      onClick={() => handleDelete(img, 'imagen')}
                      title="Eliminar"
                    >
                      <i className="bi bi-trash"></i>
                    </button>
                  </div>

                  {/* Info */}
                  <div className="px-2 py-1 bg-light" style={{ fontSize: '0.7rem' }}>
                    {img.width && img.height ? `${img.width}x${img.height}` : 'PNG'}
                    {img.size_bytes ? ` - ${formatBytes(img.size_bytes)}` : ''}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-3 bg-light rounded border border-dashed">
            <i className="bi bi-image text-muted fs-1 d-block mb-2"></i>
            <p className="text-muted mb-0">Sin fotos. Haz clic en "Agregar Fotos" para subir.</p>
          </div>
        )}
      </div>

      {/* ═══ SECCIÓN VIDEOS ═══ */}
      <div>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h6 className="mb-0">
              <i className="bi bi-camera-video me-2"></i>
              Videos
              <span className="badge bg-success ms-2">{stats.videos_count} / {stats.videos_max}</span>
            </h6>
            <small className="text-muted">MP4 o 3GP (compatible WhatsApp) - Max 50MB</small>
          </div>
          <button
            type="button"
            className="btn btn-success btn-sm"
            onClick={() => videoInputRef.current?.click()}
            disabled={uploading || stats.videos_count >= stats.videos_max}
          >
            <i className="bi bi-plus-lg me-1"></i>Agregar Video
          </button>
          <input
            type="file"
            ref={videoInputRef}
            accept="video/mp4,video/3gpp,video/3gp,.mp4,.3gp"
            onChange={handleVideoUpload}
            className="d-none"
          />
        </div>

        {/* Progress bar video */}
        {uploadProgress && uploadProgress.type === 'video' && (
          <div className="progress mb-3" style={{ height: '20px' }}>
            <div
              className="progress-bar progress-bar-striped progress-bar-animated bg-success"
              style={{ width: uploadProgress.current > 0 ? '100%' : '50%' }}
            >
              {uploadProgress.current > 0 ? 'Completado' : 'Subiendo video...'}
            </div>
          </div>
        )}

        {/* Lista de videos */}
        {videos.length > 0 ? (
          <div className="row g-2">
            {videos.map((video, idx) => (
              <div key={video.id} className="col-6 col-md-4">
                <div className="media-card position-relative rounded border overflow-hidden">
                  <div
                    className="bg-dark d-flex align-items-center justify-content-center position-relative"
                    style={{ height: '100px', cursor: 'pointer' }}
                    onClick={() => openVideoLightbox(idx)}
                  >
                    <video
                      src={getImageUrl(video.url)}
                      className="w-100 h-100"
                      style={{ objectFit: 'cover' }}
                      muted
                      preload="metadata"
                    />
                    <div className="position-absolute" style={{ background: 'rgba(0,0,0,0.4)', borderRadius: '50%', width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <i className="bi bi-play-fill text-white fs-5"></i>
                    </div>
                    {video.duration_seconds && (
                      <span className="position-absolute bottom-0 end-0 badge bg-dark m-1" style={{ fontSize: '0.65rem' }}>
                        {formatDuration(video.duration_seconds)}
                      </span>
                    )}
                  </div>
                  <div className="d-flex justify-content-between align-items-center px-2 py-1 bg-light">
                    <small style={{ fontSize: '0.7rem' }} className="text-truncate">
                      {video.original_filename || 'Video'}
                      {video.size_bytes ? ` (${formatBytes(video.size_bytes)})` : ''}
                    </small>
                    <button
                      type="button"
                      className="btn btn-sm text-danger p-0 px-1"
                      onClick={() => handleDelete(video, 'video')}
                      title="Eliminar video"
                    >
                      <i className="bi bi-trash"></i>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-3 bg-light rounded border border-dashed">
            <i className="bi bi-camera-video text-muted fs-1 d-block mb-2"></i>
            <p className="text-muted mb-0">Sin videos. Haz clic en "Agregar Video" para subir.</p>
          </div>
        )}
      </div>

      {/* Uploading overlay */}
      {uploading && (
        <div className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
             style={{ background: 'rgba(0,0,0,0.3)', zIndex: 1050 }}>
          <div className="bg-white rounded-3 p-4 text-center shadow">
            <div className="spinner-border text-primary mb-2" role="status"></div>
            <p className="mb-0">
              {uploadProgress
                ? `Subiendo ${uploadProgress.type === 'imagen' ? 'imagen' : 'video'} ${uploadProgress.current} de ${uploadProgress.total}...`
                : 'Procesando...'}
            </p>
          </div>
        </div>
      )}

      {/* Lightbox */}
      {lightboxData && (
        <ImageLightbox
          items={lightboxData.items}
          initialIndex={lightboxData.initialIndex}
          onClose={() => setLightboxData(null)}
        />
      )}

      {/* Estilos inline */}
      <style>{`
        .media-card:hover .media-overlay {
          opacity: 1 !important;
        }
        .media-overlay {
          opacity: 0;
          transition: opacity 0.2s;
        }
        .media-card:hover .media-overlay {
          opacity: 1;
        }
        .border-dashed {
          border-style: dashed !important;
        }
        .media-card[draggable="true"]:active {
          cursor: grabbing;
        }
      `}</style>
    </div>
  );
};

export default ProductMediaManager;
